import { createApp } from 'vue'
import axios from 'axios';
import * as Sentry from "@sentry/vue";
import { createSentryPiniaPlugin } from "@sentry/vue";
import vuetify from '@/vuetify'
import { createPinia } from "pinia";
import { SUPPORTED_LANGUAGES } from '@/config';
import { createI18n } from "vue-i18n";

import App from './App.vue'
import router from '@/router';

import './style.css'
import 'roboto-fontface/css/roboto/roboto-fontface.css'
import "leaflet/dist/leaflet.css";

// Vue App Creation
const app = createApp(App)

// Sentry
Sentry.init({
  app,
  dsn: "https://d82fe0b945e5c5481fe573e955404e6a@o4510759476920320.ingest.de.sentry.io/4511145168994384",
  environment: import.meta.env.MODE, // development, production or --mode
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration({
      // If you need to customize masking, ignoring and blocking in replays, check the docs.
      // https://docs.sentry.io/platforms/javascript/session-replay/privacy/
      // There are special mask/ignore/block CSS selector and class attributes that can be used without change here.
      maskAllText: false,
    }),
    Sentry.vueIntegration({
      tracingOptions: {
        trackComponents: [
          // Track only components with logic or meaning in them,
          //  not just graphical ones to reduce the number of spans produced
          // Root
          'App',
          // Components.Common.Overlay
          'ErrorOverlay',
          'InvalidCardOverlay',
          'LoadingOverlay',
          'NoQrcodeOverlay',
          'OfflineOverlay',
          'WarningOverlay',
          // Views
          'CardAccess',
          'DefaultView',
          'LoginScreen',
          'UserAccess',
          // Views.Charge
          'ChargeStatus',
          'ChargeView',
          'ConfirmationSheet',
          // Views.Payment.Partials
          'StripeCheckout',
        ]
      }
    })
  ],
  // Tracing
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  // Filter Spans
  ignoreSpans: [
    // OpenStreetMap tiles
    // {op: /^resource/, name: /openstreetmap\.org/},
    // Resource fetch for images, CSS, script, png/icons, stripe iframe
    {op: /^resource.(img|css|script|other|iframe)$/ },
  ],
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/api\.barracuda\.rossinienergy\.com\//, /^https:\/\/api\.charge\.re\//],
  // Session Replay
  replaysSessionSampleRate: 0, // Disable it, too costly
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  // Logs (console.log is not forwarded by default)
  enableLogs: true,
});

// Axios
axios.defaults.baseURL = import.meta.env.VITE_APP_API_HOST;
axios.defaults.withCredentials = true;
axios.defaults.xsrfCookieName = "csrftoken";
axios.defaults.xsrfHeaderName = "X-CSRFToken";
axios.defaults.timeout = import.meta.env.VITE_APP_DEFAULT_REQUEST_TIMEOUT || 30000;
console.log("Base URL is ", axios.defaults.baseURL)

// Pinia
const pinia = createPinia();
pinia.use(createSentryPiniaPlugin());

// I18n
function navigator_lang(lang: string | null) : string | null {
  // const lang = navigator.language || null;
  if (lang === null) { lang = navigator.language || null; }
  if (lang === null) { return null; }
  const tmp = lang.slice(0, 2).toLowerCase();
  if (!Object.keys(SUPPORTED_LANGUAGES).includes(tmp)) {
    return null;
  }
  return tmp;
}
let locale: string | null = navigator_lang(localStorage.getItem("rossini_app_language") ?? null) || "en";
localStorage.setItem("rossini_app_language", locale);
const i18n = createI18n({
  locale,
  legacy: false,

  globalInjection: true,
  allowComposition: true,

  messages: {
    en: {},
    it: {},
    fr: {},
    de: {},
    nl: {},
    es: {}
  }
});

// Vue App mount
app
  .use(pinia)
  .use(i18n)
  .use(vuetify)
  .use(router)
  .mount('#app');
